import React, { useState } from 'react';
import { ChartBar, ChartLine, TrendUp } from '@phosphor-icons/react';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ClaimsChart, RevenueChart, StatusChart } from './trends/ChartPanels';
import ComparisonCards from './trends/ComparisonCards';
import TrendsToolbar from './trends/TrendsToolbar';
import { useTrends } from './trends/useTrends';

/**
 * Self-contained trends widget — toolbar, comparison KPIs, and three charts
 * (revenue / claims / status). All data fetching + export logic lives in
 * `useTrends`; the body is purely composition.
 */
const TrendCharts = ({ agencyId = null, showControls = true }) => {
  const t = useTrends({ agencyId });
  const [activeChart, setActiveChart] = useState('revenue');

  if (t.loading && t.claimsTrend.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {showControls && (
        <TrendsToolbar
          days={t.days}
          setDays={t.setDays}
          granularity={t.granularity}
          setGranularity={t.setGranularity}
          compareTo={t.compareTo}
          setCompareTo={t.setCompareTo}
          loading={t.loading}
          exporting={t.exporting}
          onRefresh={t.refresh}
          onExportCsv={t.exportCsv}
          onExportPdf={t.exportPdf}
        />
      )}

      <ComparisonCards comparison={t.comparison} />

      <Tabs value={activeChart} onValueChange={setActiveChart}>
        <TabsList>
          <TabsTrigger value="revenue" className="gap-1">
            <TrendUp size={14} />
            Revenue
          </TabsTrigger>
          <TabsTrigger value="claims" className="gap-1">
            <ChartLine size={14} />
            Claims
          </TabsTrigger>
          <TabsTrigger value="status" className="gap-1">
            <ChartBar size={14} />
            Status
          </TabsTrigger>
        </TabsList>

        <TabsContent value="revenue">
          <RevenueChart data={t.revenueTrend} granularity={t.granularity} />
        </TabsContent>
        <TabsContent value="claims">
          <ClaimsChart data={t.claimsTrend} granularity={t.granularity} />
        </TabsContent>
        <TabsContent value="status">
          <StatusChart data={t.claimsTrend} granularity={t.granularity} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TrendCharts;
